import React from 'react';
import { List as RecordStatusesList } from '../../pms/pms-record-statuses';
import { List as CriticalitiesList } from '../../pms/pms-criticalities';
import { List as EquipmentStatusesList } from '../../pms/pms-equipment-statuses';
import { List as MaintenanceStrategiesList } from '../../pms/pms-maintenance-strategies';
import { List as JobTypesList } from '../../pms/pms-job-types';
import { List as ScheduleTypesList } from '../../pms/pms-schedule-types';
import { List as IntervalUnitsList } from '../../pms/pms-interval-units';
import { List as WorkOrderStatusesList } from '../../pms/pms-work-order-statuses';
import { List as WorkOrderSourcesList } from '../../pms/pms-work-order-sources';
import { List as ApprovalStatusesList } from '../../pms/pms-approval-statuses';
import { List as StepStatusesList } from '../../pms/pms-step-statuses';
import { List as CounterTypesList } from '../../pms/pms-counter-types';
import { List as CounterSourcesList } from '../../pms/pms-counter-sources';
import { List as MeasurementResultsList } from '../../pms/pms-measurement-results';
import { List as DefectSeveritiesList } from '../../pms/pms-defect-severities';
import { List as DefectStatusesList } from '../../pms/pms-defect-statuses';
import { List as EquipmentInstanceStatusesList } from '../../pms/pms-equipment-instance-statuses';
import { List as SafetyRequirementTypesList } from '../../pms/pms-safety-requirement-types';
import { List as DocumentTypesList } from '../../pms/pms-document-types';
import { List as VerificationStatusesList } from '../../pms/pms-verification-statuses';
import { List as DepartmentTypesList } from '../../pms/pms-department-types';
import {
  ReferenceCategoryWorkspace,
  type ReferenceNavItem,
} from './ReferenceCategoryWorkspace';

const BASE = '/parameters/pms';

const ITEMS: ReferenceNavItem[] = [
  {
    id: 'record-statuses',
    label: 'Record statuses',
    description: 'PMS record lifecycle states',
    group: 'Records',
    permissionModuleKey: 'pms_record_statuses',
  },
  {
    id: 'document-types',
    label: 'Document types',
    description: 'PMS document classifications',
    group: 'Records',
    permissionModuleKey: 'pms_document_types',
  },
  {
    id: 'verification-statuses',
    label: 'Verification statuses',
    description: 'Verification / sign-off states',
    group: 'Records',
    permissionModuleKey: 'pms_verification_statuses',
  },
  {
    id: 'equipment-statuses',
    label: 'Equipment statuses',
    description: 'Equipment operational states',
    group: 'Equipment',
    permissionModuleKey: 'pms_equipment_statuses',
  },
  {
    id: 'equipment-instance-statuses',
    label: 'Equipment instance statuses',
    description: 'Installed equipment instance states',
    group: 'Equipment',
    permissionModuleKey: 'pms_equipment_instance_statuses',
  },
  {
    id: 'criticalities',
    label: 'Criticalities',
    description: 'Priority / criticality levels',
    group: 'Equipment',
    permissionModuleKey: 'pms_criticalities',
  },
  {
    id: 'department-types',
    label: 'Department types',
    description: 'Deck, engine, and other departments',
    group: 'Equipment',
    permissionModuleKey: 'pms_department_types',
  },
  {
    id: 'maintenance-strategies',
    label: 'Maintenance strategies',
    description: 'How maintenance is planned',
    group: 'Planning',
    permissionModuleKey: 'pms_maintenance_strategies',
  },
  {
    id: 'job-types',
    label: 'Job types',
    description: 'Kinds of maintenance jobs',
    group: 'Planning',
    permissionModuleKey: 'pms_job_types',
  },
  {
    id: 'schedule-types',
    label: 'Schedule types',
    description: 'Calendar / counter schedules',
    group: 'Planning',
    permissionModuleKey: 'pms_schedule_types',
  },
  {
    id: 'interval-units',
    label: 'Interval units',
    description: 'Hours, days, months, etc.',
    group: 'Planning',
    permissionModuleKey: 'pms_interval_units',
  },
  {
    id: 'safety-requirement-types',
    label: 'Safety requirement types',
    description: 'Safety / permit requirement kinds',
    group: 'Planning',
    permissionModuleKey: 'pms_safety_requirement_types',
  },
  {
    id: 'work-order-statuses',
    label: 'Work order statuses',
    description: 'Work order lifecycle states',
    group: 'Work orders',
    permissionModuleKey: 'pms_work_order_statuses',
  },
  {
    id: 'work-order-sources',
    label: 'Work order sources',
    description: 'How work orders are raised',
    group: 'Work orders',
    permissionModuleKey: 'pms_work_order_sources',
  },
  {
    id: 'approval-statuses',
    label: 'Approval statuses',
    description: 'PMS approval workflow states',
    group: 'Work orders',
    permissionModuleKey: 'pms_approval_statuses',
  },
  {
    id: 'step-statuses',
    label: 'Step statuses',
    description: 'Job / checklist step states',
    group: 'Work orders',
    permissionModuleKey: 'pms_step_statuses',
  },
  {
    id: 'counter-types',
    label: 'Counter types',
    description: 'Running hours, cycles, etc.',
    group: 'Counters',
    permissionModuleKey: 'pms_counter_types',
  },
  {
    id: 'counter-sources',
    label: 'Counter sources',
    description: 'Where counter readings come from',
    group: 'Counters',
    permissionModuleKey: 'pms_counter_sources',
  },
  {
    id: 'measurement-results',
    label: 'Measurement results',
    description: 'Pass / fail / reading outcomes',
    group: 'Counters',
    permissionModuleKey: 'pms_measurement_results',
  },
  {
    id: 'defect-severities',
    label: 'Defect severities',
    description: 'Defect severity levels',
    group: 'Defects',
    permissionModuleKey: 'pms_defect_severities',
  },
  {
    id: 'defect-statuses',
    label: 'Defect statuses',
    description: 'Defect lifecycle states',
    group: 'Defects',
    permissionModuleKey: 'pms_defect_statuses',
  },
];

function renderPanel(section: string) {
  switch (section) {
    case 'record-statuses':
      return <RecordStatusesList />;
    case 'criticalities':
      return <CriticalitiesList />;
    case 'equipment-statuses':
      return <EquipmentStatusesList />;
    case 'maintenance-strategies':
      return <MaintenanceStrategiesList />;
    case 'job-types':
      return <JobTypesList />;
    case 'schedule-types':
      return <ScheduleTypesList />;
    case 'interval-units':
      return <IntervalUnitsList />;
    case 'work-order-statuses':
      return <WorkOrderStatusesList />;
    case 'work-order-sources':
      return <WorkOrderSourcesList />;
    case 'approval-statuses':
      return <ApprovalStatusesList />;
    case 'step-statuses':
      return <StepStatusesList />;
    case 'counter-types':
      return <CounterTypesList />;
    case 'counter-sources':
      return <CounterSourcesList />;
    case 'measurement-results':
      return <MeasurementResultsList />;
    case 'defect-severities':
      return <DefectSeveritiesList />;
    case 'defect-statuses':
      return <DefectStatusesList />;
    case 'equipment-instance-statuses':
      return <EquipmentInstanceStatusesList />;
    case 'safety-requirement-types':
      return <SafetyRequirementTypesList />;
    case 'document-types':
      return <DocumentTypesList />;
    case 'verification-statuses':
      return <VerificationStatusesList />;
    case 'department-types':
      return <DepartmentTypesList />;
    default:
      return null;
  }
}

const PmsReferenceWorkspace: React.FC = () => (
  <ReferenceCategoryWorkspace
    basePath={BASE}
    eyebrow="PMS"
    items={ITEMS}
    defaultSection="record-statuses"
    renderPanel={renderPanel}
  />
);

export default PmsReferenceWorkspace;
